import type { PogAgentTranslations } from "../en/PogAgent";
import type { TranslationShape } from "../../store/types";

export const pogAgentEs: TranslationShape<PogAgentTranslations> = {
  title: "Agente POG",
  reviewCopy: {
    details: "Detalles",
    returnToSaved: "Volver al guardado",
    partialSearch:
      "La búsqueda terminó antes de probar todas las opciones. Aún puede ser posible una corrección.",
    previewReady: "Vista previa lista. Revisa los cambios antes de aplicarlos.",
    whyFix: "Por qué esta corrección",
    cappingChanged: "Cambió la orientación del apilado",
    sideCaps: "apilado lateral",
    topCaps: "apilado superior",
    rearCaps: "apilado posterior",
    preview: "Vista previa",
    answer: "Respuesta",
    saved: "Guardado",
  },
  close: "Cerrar el agente POG",
  target: "Merchflow {{merchflowId}} · Tienda {{storeCode}} · POG {{storePogId}}",
  intro:
    "Pide un cambio en este planograma o haz una pregunta sobre él. Los cambios se previsualizan " +
    "primero; tú decides si aplicarlos a esta tienda y luego a todas las tiendas del merchflow.",
  working: "Trabajando...",
  applying: "Aplicando… {{done}} de {{total}} planogramas escritos",
  banner: {
    previewOnCanvas:
      "El planograma muestra esta previsualización como un cambio sin guardar. Revierte para " +
      "volver al diseño guardado.",
    blocked:
      "Guarda o revierte tus cambios primero: el agente trabaja sobre el planograma guardado.",
  },
  composer: {
    placeholder: "Describe el cambio, p. ej. mover el artículo 210416 al final de su estante",
    hint: "Un mensaje solo previsualiza. No se escribe nada hasta que apliques.",
    send: "Enviar",
  },
  actions: {
    applyPog: "Aplicar a este POG de tienda",
    applyPogAsNew: "Aplicar como nueva versión",
    applyMerchflow: "Aplicar a todas las tiendas del merchflow (último POG)",
    retryPog: "Reintentar aplicar a este POG de tienda",
    resumeMerchflow: "Reanudar desde el planograma {{next}} de {{total}}",
    showOnCanvas: "Mostrar en el planograma",
    onCanvas: "En el planograma",
    rerun: "Volver a previsualizar",
  },
  mode: {
    preview: "previsualización",
    apply: "aplicación",
  },
  scope: {
    pog: "este POG de tienda",
    merchflow: "todo el merchflow",
  },
  card: {
    noMessage: "(sin mensaje)",
    model: "modelo: {{model}}",
    stale:
      "El planograma se guardó después de crear esta previsualización, así que ya no coincide " +
      "con el diseño guardado. Vuelve a ejecutarla para ver el cambio sobre el planograma actual.",
    stoppedEarly:
      "Se detuvo tras {{done}} de {{total}} planogramas: {{error}} Todo lo anterior quedó " +
      "escrito; reanuda para terminar el resto sin repetirlo.",
    mergedSummary: "Aplicado a {{total}} planogramas: {{parts}}.",
  },
  intent: {
    postprocess: "Edición",
    qa: "Pregunta",
    diagnose: "Diagnóstico",
  },
  picker: {
    question:
      "No pude saber si eso es un cambio en el planograma o una pregunta sobre él. ¿Cuál es?",
    postprocess: "Editar el planograma",
    qa: "Hacer una pregunta",
    diagnose: "Diagnosticar (próximamente)",
  },
  diff: {
    none: "Sin cambios de diseño.",
    change: "{{count}} cambio de diseño",
    changes: "{{count}} cambios de diseño",
    showAll: "Mostrar los {{count}}",
    showLess: "Mostrar menos",
    position: "posición {{from}} → {{to}}",
    shelf: "estante {{from}} → {{to}}",
    addedToShelf: "añadido al estante {{shelf}}",
    removedFromShelf: "retirado del estante {{shelf}}",
    facings: "frentes {{from}} → {{to}}",
    rows: "filas {{from}} → {{to}}",
    orientation: "orientación {{from}} → {{to}}",
    squeezeWidth: "compresión de ancho {{from}} → {{to}}",
    squeezeHeight: "compresión de alto {{from}} → {{to}}",
    squeezeDepth: "compresión de fondo {{from}} → {{to}}",
    default: "predeterminada",
    on: "activada",
    off: "desactivada",
    updated: "actualizado",
  },
  results: {
    written: "escrito",
    newCreated: "guardado como nuevo POG",
    skipped: "omitido — sin cambios",
    failed: "falló",
    store: "Tienda {{storeCode}}",
    summary: "{{parts}} de {{total}} planogramas",
    countWritten: "{{count}} escritos",
    countNewCreated: "{{count}} guardados como nuevos",
    countSkipped: "{{count}} omitidos",
    countFailed: "{{count}} fallidos",
    showAll: "Mostrar los {{count}}",
    showLess: "Mostrar menos",
  },
  errors: {
    agent: "Error del agente ({{status}}): {{detail}}",
    unreachable: "No se pudo contactar con el agente POG: {{detail}}",
    batchStalled: "El lote de aplicación del agente no avanzó; se detiene para evitar un bucle.",
    drawFailed: "No se pudo dibujar este diseño: {{detail}}",
  },
  page: {
    openAgent: "Agente POG",
    replaceTitle: "¿Reemplazar el planograma con la versión del agente?",
    replaceDescription:
      "El agente trabaja sobre el planograma guardado y no puede ver los cambios que hiciste " +
      "sobre su previsualización. Esos cambios se perderán.",
    keepMyChanges: "Conservar mis cambios",
    replace: "Reemplazar",
  },
  embedded: {
    title: "Corregir con IA",
    context:
      "La IA usa el plan actual y el módulo seleccionado. Los cambios se quedan en esta tienda.",
    emptyTitle: "¿Qué le gustaría cambiar?",
    emptyBody:
      "Describa un cambio para este planograma. Puede revisar la propuesta antes de guardar.",
    emptyExample: "Ejemplo: “Mantén el orden de los productos y rellena los huecos con side caps.”",
    introPreviewOnly:
      "Describa qué cambiar. Compare la distribución propuesta antes de guardar una corrección.",
    intro:
      "Describa lo que quiere cambiar o pregunte sobre este plan. Revise una vista previa antes de aplicar una corrección.",
    failure: {
      unreachable: "La IA no pudo conectarse. Reintente cuando el servicio local esté disponible.",
      planChanged: "El plan guardado cambió. Recárguelo y vuelva a solicitar la corrección.",
      session: "Su sesión necesita actualizarse. Recargue la página e inténtelo de nuevo.",
      generic:
        "La IA no pudo preparar esta corrección. Inténtelo de nuevo o describa el cambio de otra manera.",
    },
  },
};
